import { StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { ApiError, locale as localeDefinition, negotiateLocale, type Locale } from "@tovi/shared";
import { I18nProvider, useBrandTokens } from "@tovi/ui";
import "./styles.css";
import { SignInScreen } from "@/screens/sign-in.tsx";
import { HomeScreen } from "@/screens/home.tsx";
import { ApproveScreen } from "@/screens/approve.tsx";
import { authClient } from "@/lib/auth-client.ts";
import { api, onSignedOut, setToken, token } from "@/lib/api.ts";

/**
 * The employee app (`#166`, and the till-approval screen of `#213`).
 *
 * Sign in, then a home screen — no router yet, the same way `apps/pos` has
 * none: there is exactly one thing to show once signed in, and everything
 * this app grows into opens over it. `/approve?user_code=…` is the one
 * exception, and still not a route: it is read once at load into
 * `pendingCode`, which decides whether the sign-in or the home screen's place
 * is taken by the approval screen instead. The till's own QR encodes exactly
 * that URL (the concurrent issue), and a phone's camera opens it in the
 * browser with no in-app scanner involved.
 */

type Session = "none" | "live";

function readPendingCode(): string | null {
  if (location.pathname !== "/approve") return null;
  return new URLSearchParams(location.search).get("user_code");
}

const client = new QueryClient({
  defaultOptions: {
    queries: {
      // A 401 is not retried: the second attempt fails the same way and only
      // delays the sign-in screen (`apps/pos/src/main.tsx`).
      retry: (attempts, error) =>
        !(error instanceof ApiError && error.code === "UNAUTHENTICATED") && attempts < 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const [session, setSession] = useState<Session>(() => (token() === null ? "none" : "live"));
  const [locale, setLocale] = useState<Locale>(() =>
    negotiateLocale(navigator.languages.join(",")),
  );
  const [pendingCode, setPendingCode] = useState<string | null>(readPendingCode);

  // `["me"]` is the same query key `home.tsx` and `approve.tsx` fetch with, so
  // this shares their cached request rather than firing a second one.
  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => api<{ brand: { colorTokens: Record<string, string> } }>("/me"),
    enabled: session === "live",
  });
  useBrandTokens(me.data?.brand);

  useEffect(() => onSignedOut(() => setSession("none")), []);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = localeDefinition(locale).direction;
  }, [locale]);

  async function signOut() {
    // Best-effort: the token is cleared locally either way, since a person
    // asking to be signed out must not be told to try again because the
    // network is not.
    await authClient.signOut().catch(() => {});
    setToken(null);
    setSession("none");
  }

  function enterCode(userCode: string) {
    history.pushState(null, "", `/approve?user_code=${encodeURIComponent(userCode)}`);
    setPendingCode(userCode);
  }

  function finishApproval() {
    history.replaceState(null, "", "/");
    setPendingCode(null);
  }

  return (
    <I18nProvider locale={locale}>
      {session === "none" ? (
        <SignInScreen
          locale={locale}
          onLocaleChange={setLocale}
          continuingApproval={pendingCode !== null}
          onSignedIn={() => {
            setSession("live");
            void client.invalidateQueries();
          }}
        />
      ) : pendingCode !== null ? (
        <ApproveScreen userCode={pendingCode} onFinished={finishApproval} />
      ) : (
        <HomeScreen onSignedOut={signOut} onEnterCode={enterCode} />
      )}
    </I18nProvider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={client}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);
